// ===== Packages =====

import React, {
    useEffect,
    useRef,
    useState,
    useMemo,
}                               from 'react';
import { createPortal }         from 'react-dom';
import { ReactSVG }             from 'react-svg';

// ===== Components =====

import {
    Button,
}                               from '../Editor/helpers';
// eslint-disable-next-line import/order
import { Transition }           from '../Transition';

// ===== Services =====

import {
    playAudio,
    getPressEventHandlers,
    getTouchMouseEventHandlers,
}                               from '../../services';
import useLazyAudioRef           from '../../services/useLazyAudioRef';

// ===== Hooks =====

import {
    useTimeout,
}                               from '../../hooks';

// ===== Interfaces =====

import {
    ISnackbarItem,
}                               from '../../interfaces';

// ===== Enums =====

import {
    BUTTON_TYPE,
    CURSOR_TARGET,
    INTERACTABLE_OBJECT,
}                               from '../../enums';

// ===== Images =====

import CrossIcon                from '../../images/cross.svg';

// ===== Sounds =====

import SnackbarEnter            from '../../sounds/swoosh_in.mp3';
import SnackbarExit             from '../../sounds/swoosh_out.mp3';

// ===== Constants =====

import {
    BODY_FONT_SIZE,
    DEFAULT_AUDIO_VOLUME,
    FADE_IN_DEFAULT_STYLE,
    FADE_IN_TRANSITION_STYLES,
    HOVER_TARGET_CLASSNAME,
    SNACKBAR_ENTER_DURATION,
    SNACKBAR_EXIT_DURATION,
}                               from '../../constants/generalConstants';
import CURSOR_SIGN              from '../../constants/cursorSigns';

// ===== Styles =====

import {
    Container,
    SnackbarIcon,
    SnackbarText,
    DismissButtonContainer,
}                               from './styles';
import { theme }                from '../../themes/theme-context';

interface Props {
    data: ISnackbarItem,
    customCursorActive: boolean,
    hasSound: boolean,
    setSnackbarData: React.Dispatch<React.SetStateAction<ISnackbarItem>>,
    onCursorEnter: (
        targetType: CURSOR_TARGET | INTERACTABLE_OBJECT | string,
        actions: string[],
        candidateTarget?: HTMLElement,
    ) => void,
    onCursorLeave: (e?: React.MouseEvent | React.TouchEvent | React.SyntheticEvent) => void,
}
function Snackbar({
    data,
    customCursorActive,
    hasSound,
    setSnackbarData,
    onCursorEnter,
    onCursorLeave,
}: Props): JSX.Element | null {
    // ===== General Constants =====

    const CENTER_TEXT_LENGTH_THRESHOLD = 2.5 * BODY_FONT_SIZE;
    const DISMISS_BUTTON_LENGTH = 1.875 * BODY_FONT_SIZE;
    const DEFAULT_SNACKBAR_BOTTOM_OFFSET = 3.125 * BODY_FONT_SIZE;

    // ===== Refs =====

    // ----- Sound Clips
    const enterClip = useLazyAudioRef({
        src: SnackbarEnter,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const exitClip = useLazyAudioRef({
        src: SnackbarExit,
        volume: DEFAULT_AUDIO_VOLUME,
    });
    const dismissTimedSnackbarTimeoutRef = useRef<number | null>(null);
    // Guards against queue dequeue races while exit animation is in-flight.
    const exitingRef = useRef<boolean>(false);
    const snackbarQueueRef = useRef<ISnackbarItem[]>([]);
    const activeSuppressedReplayKeyRef = useRef<string | null>(null);

    // ===== State =====

    const [visible, setVisible] = useState<boolean>(data.visible);
    const [text, setText] = useState<string | null>(null);
    const [icon, setIcon] = useState<string | null>(null);
    const [hasSuccess, setHasSuccess] = useState<boolean>(false);
    const [hasFailure, setHasFailure] = useState<boolean>(false);
    const [dismissable, setDismissable] = useState<boolean>(false);
    const [bottomOffset, setBottomOffset] = useState<number>(DEFAULT_SNACKBAR_BOTTOM_OFFSET);
    const [resetTimeoutIsActive, setResetTimeoutIsActive] = useState<boolean>(false);
    const [snackbarQueue, setSnackbarQueue] = useState<ISnackbarItem[]>([]);

    // ===== Animation Constants =====

    const SNACKBAR_RESET_TIMEOUT_DURATION = SNACKBAR_EXIT_DURATION + 100;

    // ===== Methods =====

    /**
     * Manages response to button hovering over
     * @param e mouse event
     */
    const onButtonMouseEnter = (e: React.MouseEvent): void => {
        onCursorEnter(
            CURSOR_TARGET.snackbarCloseButon,
            [CURSOR_SIGN.click],
            e.currentTarget as HTMLElement,
        );
    };

    /**
     * Manages response to button no longer hovering over
     * @param e mouse event
     */
    const onButtonMouseLeave = (e: React.MouseEvent): void => {
        onCursorLeave(e);
    };

    const executeSnackbar = (item: ISnackbarItem): void => {
        const suppressedReplayKey = item.suppressReplay
            ? `${item.text}:${item.icon || ''}:${!!item.hasSuccess}:${!!item.hasFailure}:${!!item.dismissable}:${item.bottomOffset || DEFAULT_SNACKBAR_BOTTOM_OFFSET}`
            : null;

        if (
            suppressedReplayKey != null
            && activeSuppressedReplayKeyRef.current === suppressedReplayKey
        ) {
            return;
        }

        activeSuppressedReplayKeyRef.current = suppressedReplayKey;
        exitingRef.current = false;
        clearTimeoutResetSnackbar();
        setVisible(true);
        setText(item.text);
        setIcon(item.icon || null);
        setDismissable(!!item.dismissable);
        setHasSuccess(!!item.hasSuccess);
        setHasFailure(!!item.hasFailure);
        setBottomOffset(item.bottomOffset || DEFAULT_SNACKBAR_BOTTOM_OFFSET);

        if (dismissTimedSnackbarTimeoutRef.current != null) {
            window.clearTimeout(dismissTimedSnackbarTimeoutRef.current);
            dismissTimedSnackbarTimeoutRef.current = null;
        }
        if (item.duration && !item.hasFailure) {
            // failure snackbars are always made to be dismissable
            dismissTimedSnackbarTimeoutRef.current = window.setTimeout(() => {
                dismissSnackbar();
            }, item.duration);
        }

        // Play Sound
        if (
            hasSound
            && enterClip.current
        ) {
            enterClip.current.pause();
            enterClip.current.currentTime = 0;
            playAudio(enterClip.current);
        }
    };

    /**
     * Stores the next snackbar request as a value snapshot so repeat requests
     * are preserved even when callers reuse the same text and icon.
     */
    const queueSnackbar = (item: ISnackbarItem): void => {
        const updatedQueue = [
            ...snackbarQueueRef.current,
            {
                ...item,
            },
        ];
        snackbarQueueRef.current = updatedQueue;
        setSnackbarQueue(updatedQueue);
    };

    /**
     * Manages dismissing the snackbar
     */
    const dismissSnackbar = (e?: React.MouseEvent | React.TouchEvent): void => {
        if (e) onCursorLeave(e);
        if (dismissTimedSnackbarTimeoutRef.current != null) {
            window.clearTimeout(dismissTimedSnackbarTimeoutRef.current);
            dismissTimedSnackbarTimeoutRef.current = null;
        }
        exitingRef.current = true;
        setVisible(false);
        setResetTimeoutIsActive(true);
        clearTimeoutResetSnackbar();
        timeoutResetSnackbar();

        // Play Sound
        if (
            hasSound
            && exitClip.current
        ) {
            exitClip.current.pause();
            exitClip.current.currentTime = 0;
            playAudio(exitClip.current);
        }
    };

    // ===== Side Effects ======

    useEffect(() => function cleanup() {
        if (dismissTimedSnackbarTimeoutRef.current != null) {
            window.clearTimeout(dismissTimedSnackbarTimeoutRef.current);
        }
    }, []);

    useEffect(() => {
        const dataIcon = data.icon || null;
        const dataHasSuccess = !!data.hasSuccess;
        const dataHasFailure = !!data.hasFailure;
        const dataDismissable = !!data.dismissable;
        const dataBottomOffset = data.bottomOffset || DEFAULT_SNACKBAR_BOTTOM_OFFSET;
        const currentVisibleSnackbarMatchesData = (
            visible
            && data.visible
            && data.text === text
            && dataIcon === icon
            && dataHasSuccess === hasSuccess
            && dataHasFailure === hasFailure
            && dataDismissable === dismissable
            && dataBottomOffset === bottomOffset
        );

        if (!data.visible) {
            activeSuppressedReplayKeyRef.current = null;
            if (snackbarQueueRef.current.length > 0) {
                snackbarQueueRef.current = [];
                setSnackbarQueue([]);
            }

            if (
                visible
                && !resetTimeoutIsActive
                && !exitingRef.current
            ) {
                // Parent-driven transient snackbars, like drag/drop affordances, need
                // to dismiss immediately when the source interaction ends.
                dismissSnackbar();
            }
            return;
        }

        if (currentVisibleSnackbarMatchesData) {
            if (data.suppressReplay) {
                return;
            }

            queueSnackbar(data);
            dismissSnackbar();
            return;
        }

        if (
            (
                data.visible
                && !visible
            ) || (
                visible
                && data.visible
                && data.text !== text
            )
            || (
                visible
                && data.visible
                && data.icon !== icon
            )
            || (
                visible
                && data.visible
                && data.hasSuccess !== hasSuccess
            )
            || (
                visible
                && data.visible
                && data.hasFailure !== hasFailure
            )
        ) {
            if (resetTimeoutIsActive) {
                queueSnackbar(data);
            } else if (
                visible
                && (
                    dismissable
                    || hasFailure
                )
            ) {
                queueSnackbar(data);
                dismissSnackbar();
            } else if (visible) {
                queueSnackbar(data);
            } else {
                executeSnackbar(data);
            }
        }
    }, [data]);

    useEffect(() => {
        if (
            !visible
            && !resetTimeoutIsActive
            && !exitingRef.current
            && snackbarQueue.length > 0
        ) {
            const queue = [...snackbarQueueRef.current];
            const nextItem = queue.shift()!;
            snackbarQueueRef.current = queue;
            setSnackbarQueue(queue);
            executeSnackbar(nextItem);
        }
    }, [visible, resetTimeoutIsActive, snackbarQueue]);

    const {
        start: timeoutResetSnackbar,
        clear: clearTimeoutResetSnackbar,
    } = useTimeout(() => {
        exitingRef.current = false;
        setText(null);
        if (icon) setIcon(null);
        if (hasSuccess) setHasSuccess(false);
        if (hasFailure) setHasFailure(false);
        if (dismissable) setDismissable(false);
        if (bottomOffset !== DEFAULT_SNACKBAR_BOTTOM_OFFSET) {
            setBottomOffset(DEFAULT_SNACKBAR_BOTTOM_OFFSET);
        }
        if (resetTimeoutIsActive) setResetTimeoutIsActive(false);
        if (snackbarQueueRef.current.length === 0) {
            setSnackbarData({
                visible: false,
                text: '',
            });
        }
    }, SNACKBAR_RESET_TIMEOUT_DURATION);

    // ===== Memoizations =====

    const dismissButtonBackground = useMemo(() => {
        if (hasSuccess) return theme.verascopeColor.blue200;
        if (hasFailure) return theme.verascopeColor.red200;
        return theme.verascopeColor.purple200;
    }, [hasSuccess, hasFailure]);

    if (typeof document === 'undefined') {
        return null;
    }

    return createPortal(
        (
            <Transition
                in={visible}
                timeout={{
                    enter: SNACKBAR_ENTER_DURATION,
                    exit: SNACKBAR_EXIT_DURATION,
                }}
            >
                {(state) => (
                    <Container
                        hasSuccess={hasSuccess}
                        hasFailure={hasFailure}
                        dismissable={dismissable || hasFailure}
                        hasIcon={!!icon}
                        bottomOffset={bottomOffset}
                        style={{
                            // FADE_IN_TRANSITION_STYLES keeps full opacity for `exiting`,
                            // so map `exiting` to the hidden target style to start fade immediately.
                            // This prevents queued snackbars from appearing before the prior one
                            // has visually completed its exit.
                            ...(state === 'exiting'
                                ? FADE_IN_TRANSITION_STYLES({
                                    direction: 'up',
                                    offset: 10,
                                    horizontalCenter: true,
                                }).exited
                                : {}),
                            ...FADE_IN_DEFAULT_STYLE({
                                direction: 'up',
                                offset: 10,
                                duration: SNACKBAR_ENTER_DURATION,
                                easing: theme.motion.eagerEasing,
                                horizontalCenter: true,
                            }),
                            ...FADE_IN_TRANSITION_STYLES({
                                direction: 'up',
                                offset: 10,
                                horizontalCenter: true,
                            })[state === 'exiting' ? 'exited' : state],
                        }}
                    >
                        {icon
                        && (
                            <SnackbarIcon>
                                <ReactSVG
                                    src={icon}
                                />
                            </SnackbarIcon>
                        )}
                        <SnackbarText
                            center={!icon && (text?.length || 0) < CENTER_TEXT_LENGTH_THRESHOLD}
                            hasIcon={!!icon}
                            dismissable={dismissable || hasFailure}
                            buttonLength={DISMISS_BUTTON_LENGTH}
                        >
                            {text}
                        </SnackbarText>
                        {(dismissable || hasFailure) && (
                            <DismissButtonContainer>
                                <Button
                                    className={HOVER_TARGET_CLASSNAME}
                                    customCursorActive={customCursorActive}
                                    type={BUTTON_TYPE.solid}
                                    center
                                    height={DISMISS_BUTTON_LENGTH}
                                    width={DISMISS_BUTTON_LENGTH}
                                    icon={CrossIcon}
                                    background={dismissButtonBackground}
                                    {...getTouchMouseEventHandlers({
                                        onMouseEnter: onButtonMouseEnter,
                                        onMouseLeave: onButtonMouseLeave,
                                    })}
                                    {...getPressEventHandlers({
                                        onTouchStart: (e) => dismissSnackbar(e),
                                        onMouseDown: (e) => dismissSnackbar(e),
                                    })}
                                />
                            </DismissButtonContainer>
                        )}
                    </Container>
                )}
            </Transition>
        ),
        document.body,
    );
}

export default Snackbar;
